import React, { useState, useEffect } from 'react';
import { Bookmark, Plus, Check, FolderPlus, ChevronDown, X, Lock, Globe } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export const AddToCollectionButton = ({ recipeId, userId, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [recipeCollections, setRecipeCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');

  useEffect(() => {
    if (isOpen && userId) {
      fetchCollections();
      fetchRecipeCollections();
    }
  }, [isOpen, userId, recipeId]);

  const fetchCollections = async () => {
    try {
      const { data } = await supabase
        .from('collections')
        .select('id, name, is_public')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (data) setCollections(data);
    } catch (e) { console.error(e); }
  };

  const fetchRecipeCollections = async () => {
    try {
      const { data } = await supabase
        .from('collection_recipes')
        .select('collection_id')
        .eq('recipe_id', recipeId);
      if (data) setRecipeCollections(data.map(cr => cr.collection_id));
    } catch (e) { console.error(e); }
  };

  const toggleCollection = async (collectionId) => {
    setIsLoading(true);
    try {
      const isInCollection = recipeCollections.includes(collectionId);
      
      if (isInCollection) {
        await supabase
          .from('collection_recipes')
          .delete()
          .eq('collection_id', collectionId)
          .eq('recipe_id', recipeId);
        setRecipeCollections(prev => prev.filter(id => id !== collectionId));
      } else {
        await supabase
          .from('collection_recipes')
          .insert({ collection_id: collectionId, recipe_id: recipeId });
        setRecipeCollections(prev => [...prev, collectionId]);
      }
      
      if (onSuccess) onSuccess(isInCollection ? 'removed' : 'added');
    } catch (e) { 
      console.error(e); 
    } finally {
      setIsLoading(false);
    }
  };

  const createAndAdd = async () => {
    if (!newCollectionName.trim()) return;
    setIsLoading(true);
    
    try {
      // Create collection
      const { data: newCollection, error } = await supabase
        .from('collections')
        .insert({
          user_id: userId,
          name: newCollectionName.trim(),
          is_public: false
        })
        .select()
        .single();

      if (error) throw error;

      // Add recipe to new collection
      await supabase
        .from('collection_recipes')
        .insert({ collection_id: newCollection.id, recipe_id: recipeId });

      setCollections(prev => [newCollection, ...prev]);
      setRecipeCollections(prev => [...prev, newCollection.id]);
      setNewCollectionName('');
      setIsCreating(false);
      
      if (onSuccess) onSuccess('created');
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const isInAnyCollection = recipeCollections.length > 0;

  return (
    <div className="relative">
      <button
        data-testid="add-to-collection-btn"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-6 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all ${
          isInAnyCollection
            ? 'bg-primary text-white shadow-lg shadow-primary/20'
            : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-primary hover:text-white'
        }`}
      >
        <Bookmark size={18} fill={isInAnyCollection ? 'currentColor' : 'none'} />
        {isInAnyCollection ? 'Guardada' : 'Guardar'}
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-stone-900 rounded-3xl shadow-2xl border border-stone-100 dark:border-stone-800 overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
            <div className="p-4 border-b border-stone-100 dark:border-stone-800">
              <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
                Guardar en colección
              </p>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {collections.length > 0 ? (
                collections.map(collection => (
                  <button
                    key={collection.id}
                    onClick={() => toggleCollection(collection.id)}
                    disabled={isLoading}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                        recipeCollections.includes(collection.id)
                          ? 'bg-primary text-white'
                          : 'bg-stone-100 dark:bg-stone-700 text-stone-400'
                      }`}>
                        {recipeCollections.includes(collection.id) ? (
                          <Check size={16} />
                        ) : (
                          <Bookmark size={16} />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-stone-900 dark:text-white">
                          {collection.name}
                        </p>
                        <p className="text-[10px] text-stone-400 flex items-center gap-1">
                          {collection.is_public ? <Globe size={10} /> : <Lock size={10} />}
                          {collection.is_public ? 'Pública' : 'Privada'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-6 text-center">
                  <Bookmark size={32} className="mx-auto mb-2 text-stone-200" />
                  <p className="text-sm text-stone-400">No tienes colecciones</p>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-stone-100 dark:border-stone-800 bg-stone-50 dark:bg-stone-800/50">
              {isCreating ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    placeholder="Nombre de colección..."
                    className="flex-1 px-3 py-2 rounded-xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 text-sm font-bold outline-none focus:border-primary dark:text-white"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && createAndAdd()}
                  />
                  <button
                    onClick={createAndAdd}
                    disabled={isLoading || !newCollectionName.trim()}
                    className="p-2 bg-primary text-white rounded-xl disabled:opacity-50"
                  >
                    <Check size={18} />
                  </button>
                  <button
                    onClick={() => { setIsCreating(false); setNewCollectionName(''); }}
                    className="p-2 text-stone-400 hover:text-stone-600 rounded-xl"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsCreating(true)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-primary font-black uppercase tracking-widest text-[10px] hover:bg-primary/10 rounded-xl transition-colors"
                >
                  <FolderPlus size={16} /> Nueva Colección
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
